import { useMemo } from 'react';
import type { ReactElement } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import { Construction, Handshake, Person4, Print, ReceiptLong, RequestQuote } from "@mui/icons-material";
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHasPermission, useHasAnyPermission } from '../../../contexts/auth';

export interface MenuItem {
    onClick: () => void;
    text: string;
    translatedText: string;
    icon: ReactElement;
    permission?: string;
}

interface MenuItemData {
    textKey: string;
    icon: ReactElement;
    permission?: string;
}

const menuItemsData: MenuItemData[] = [
    {
        textKey: "Home",
        icon: <HomeIcon />,
    },
    {
        textKey: "Service",
        icon: <Construction />,
        permission: "service.view_job",
    },
    {
        textKey: "Equipment section",
        icon: <Print />,
        permission: "settlement.view_equipment",
    },
    {
        textKey: "Clients",
        icon: <Person4 />,
        permission: "settlement.view_client",
    },
    {
        textKey: "Settlements",
        icon: <RequestQuote />,
        permission: "settlement.view_settlement",
    },
    {
        textKey: "Contracts",
        icon: <Handshake />,
        permission: "settlement.view_contract",
    },
    {
        textKey: "Invoices",
        icon: <ReceiptLong />,
        permission: "invoice.view_invoice",
    },
];

const getMenuRoutes = (navigate: NavigateFunction, t: (key: string) => string): Record<string, () => void> => ({
    [t("Home")]: () => navigate('/home'),
    [t("Service")]: () => navigate('/service'),
    [t("Equipment section")]: () => navigate('/equipment-section'),
    [t("Clients")]: () => navigate('/clients'),
    [t("Settlements")]: () => navigate('/settlements'),
    [t("Contracts")]: () => navigate('/contracts'),
    [t("Invoices")]: () => navigate('/invoices'),
});

export const useMenuData = (): MenuItem[] => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Permission checks for menu items
    const canViewClients = useHasPermission("settlement.view_client");
    const canViewContracts = useHasPermission("settlement.view_contract");
    const canViewEquipment = useHasPermission("settlement.view_equipment");
    const canViewSettlements = useHasPermission("settlement.view_settlement");
    const canViewService = useHasAnyPermission(["service.view_job", "service.view_own_job"]);
    const canViewInvoices = useHasPermission("invoice.view_invoice");

    return useMemo(() => {
        const routes = getMenuRoutes(navigate, t);
        const permissionMap: Record<string, boolean> = {
            "Clients": canViewClients,
            "Service": canViewService,
            "Equipment section": canViewEquipment,
            "Settlements": canViewSettlements,
            "Contracts": canViewContracts,
            "Invoices": canViewInvoices,
        };

        return menuItemsData
            .filter(item => {
                if (!item.permission) {
                    return true;
                }
                return permissionMap[item.textKey] ?? false;
            })
            .map(item => {
                const translatedText = t(item.textKey);
                return {
                    ...item,
                    text: item.textKey,
                    translatedText: translatedText,
                    onClick: routes[translatedText],
                };
            });
    }, [canViewClients, canViewContracts, canViewEquipment, canViewSettlements, canViewService, canViewInvoices, navigate, t]);
};
